/**
 * Raíz de la consola (docs/FASE-1.md §6): `SessionProvider` + enrutado. Las rutas de la consola
 * autenticada viven bajo `Layout` (barra lateral + cabecera); `Login`, `ForgotPassword` y
 * `ResetPassword` son las únicas rutas públicas.
 */
import type { ReactElement } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { SessionProvider, useSession } from "./api/session";
import { Layout } from "./components/Layout";
import { ToastHost } from "./components/Toast";
import { Audit } from "./pages/Audit";
import { ForgotPassword } from "./pages/ForgotPassword";
import { Health } from "./pages/Health";
import { Login } from "./pages/Login";
import { ResetPassword } from "./pages/ResetPassword";
import { ModuleDetail } from "./pages/ModuleDetail";
import { Cuenta } from "./pages/Cuenta";
import { Modules } from "./pages/Modules";
import { OrganizationDetail } from "./pages/OrganizationDetail";
import { Organizations } from "./pages/Organizations";

/**
 * Protege las rutas de la consola autenticada. Mientras la sesión se restaura desde el refresh
 * token guardado (`restoreSession`, ver `api/session.ts`) muestra un estado de carga en vez de
 * redirigir de inmediato a `/login`, para no expulsar a un operador con sesión válida.
 */
function RequireSession({ children }: { children: ReactElement }): ReactElement {
  const { status } = useSession();
  const location = useLocation();

  if (status === "authenticated") return children;
  if (status === "restoring") return <p className="shell">Restaurando sesión…</p>;
  return <Navigate to="/login" replace state={{ from: location.pathname }} />;
}

export function App(): ReactElement {
  return (
    <SessionProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            element={
              <RequireSession>
                <Layout />
              </RequireSession>
            }
          >
            <Route path="/organizations" element={<Organizations />} />
            <Route path="/organizations/:orgId" element={<OrganizationDetail />} />
            <Route path="/modules" element={<Modules />} />
            <Route path="/modules/:code" element={<ModuleDetail />} />
            <Route path="/audit" element={<Audit />} />
            <Route path="/health" element={<Health />} />
            <Route path="/cuenta" element={<Cuenta />} />
            <Route path="/" element={<Navigate to="/organizations" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/organizations" replace />} />
        </Routes>
      </BrowserRouter>
      <ToastHost />
    </SessionProvider>
  );
}
